import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { FileText, Download, GraduationCap, BarChart3, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function ReportCard({ studentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (studentId) {
      api.get(`/reports/report-card/${studentId}`)
        .then(r => setData(r.data))
        .catch(() => toast.error('Failed to load report card'))
        .finally(() => setLoading(false));
    }
  }, [studentId]);

  const downloadPDF = () => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/report-card/${studentId}/pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `report_card_${data?.student?.first_name}_${data?.student?.last_name}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success('PDF downloaded');
      })
      .catch(() => toast.error('Download failed'));
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading report card...</div>;
  if (!data) return <div className="text-center py-8 text-slate-400">No report card data available</div>;

  const { student, subject_results, overall_average, attendance } = data;

  return (
    <div data-testid="report-card" className="space-y-6">
      {/* Header */}
      <div className="bg-[#1B2A4A] rounded-2xl p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-[#C9A84C] text-xs uppercase font-extrabold tracking-[0.2em] mb-1">Academic Report Card</p>
            <h2 className="text-xl font-bold">{student.first_name} {student.last_name}</h2>
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge className="bg-white/10 text-white border-white/20">{student.admission_number}</Badge>
              <Badge className="bg-[#C9A84C]/20 text-[#C9A84C] border-[#C9A84C]/30">{student.division}</Badge>
              <Badge className="bg-white/10 text-white border-white/20">{student.grade}</Badge>
              {student.class_name && <Badge className="bg-white/10 text-white border-white/20">{student.class_name}</Badge>}
            </div>
          </div>
          <Button onClick={downloadPDF} className="btn-gold" data-testid="download-report-pdf">
            <Download className="w-4 h-4 mr-1" /> Download PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card text-center">
          <BarChart3 className="w-6 h-6 text-[#C9A84C] mx-auto mb-2" />
          <p className="text-2xl font-black text-[#1B2A4A]">{overall_average}%</p>
          <p className="text-xs text-slate-500">Overall Average</p>
        </div>
        <div className="stat-card text-center">
          <GraduationCap className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
          <p className="text-2xl font-black text-emerald-600">{subject_results.length}</p>
          <p className="text-xs text-slate-500">Subjects</p>
        </div>
        <div className="stat-card text-center">
          <Calendar className="w-6 h-6 text-sky-600 mx-auto mb-2" />
          <p className="text-2xl font-black text-sky-600">{attendance.percentage}%</p>
          <p className="text-xs text-slate-500">Attendance</p>
        </div>
        <div className="stat-card text-center">
          <FileText className="w-6 h-6 text-violet-600 mx-auto mb-2" />
          <p className="text-2xl font-black text-violet-600">{subject_results.reduce((sum, s) => sum + s.assessments.length, 0)}</p>
          <p className="text-xs text-slate-500">Assessments</p>
        </div>
      </div>

      {/* Subject Results */}
      <Card>
        <CardHeader><CardTitle className="text-lg text-[#1B2A4A]">Subject Results</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="data-table">
                <TableHead>Subject</TableHead><TableHead>Marks</TableHead><TableHead>Total</TableHead><TableHead>Percentage</TableHead><TableHead>Grade</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subject_results.map((sr, i) => (
                <TableRow key={i}>
                  <TableCell className="font-semibold text-[#1B2A4A]">{sr.subject_name}</TableCell>
                  <TableCell>{sr.total_marks}</TableCell>
                  <TableCell>{sr.total_possible}</TableCell>
                  <TableCell className="font-bold">{sr.average_percentage}%</TableCell>
                  <TableCell>
                    <Badge className={sr.final_grade === 'F' ? 'bg-red-100 text-red-700' : sr.final_grade.startsWith('A') ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'}>
                      {sr.final_grade}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {subject_results.length > 0 && (
                <TableRow className="bg-[#1B2A4A]/5 font-bold">
                  <TableCell className="text-[#1B2A4A]">Overall Average</TableCell>
                  <TableCell colSpan={2} />
                  <TableCell className="text-[#C9A84C] font-black">{overall_average}%</TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Attendance Summary */}
      <Card>
        <CardHeader><CardTitle className="text-lg text-[#1B2A4A]">Attendance Summary</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 text-center">
            <div className="bg-slate-50 rounded-lg p-3"><p className="text-lg font-black">{attendance.total}</p><p className="text-xs text-slate-500">Total Days</p></div>
            <div className="bg-emerald-50 rounded-lg p-3"><p className="text-lg font-black text-emerald-600">{attendance.present}</p><p className="text-xs text-slate-500">Present</p></div>
            <div className="bg-amber-50 rounded-lg p-3"><p className="text-lg font-black text-amber-600">{attendance.late}</p><p className="text-xs text-slate-500">Late</p></div>
            <div className="bg-red-50 rounded-lg p-3"><p className="text-lg font-black text-red-600">{attendance.absent}</p><p className="text-xs text-slate-500">Absent</p></div>
          </div>
        </CardContent>
      </Card>

      {/* Teacher Comments */}
      {data.teacher_comments?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg text-[#1B2A4A]">Teacher Comments</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.teacher_comments.map((tc, i) => (
                <div key={i} className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-bold text-sm text-[#1B2A4A]">{tc.teacher_name}</p>
                    {tc.term && <Badge variant="outline" className="text-[10px]">{tc.term}</Badge>}
                  </div>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{tc.comment}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
